import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import { Trash } from '@phosphor-icons/react';
import { useError } from 'client/lib/Error';
import { useApi } from 'client/lib/trpc';
import { useAccount, useUser } from 'client/lib/User';
import { useProjectState } from 'client/state/ProjectState';
import { Button } from 'client/components/Button';
import { useConfirmDelete } from 'client/components/Dialog/DeleteDialog';

export const ProjectActions = () => {
  const api = useApi();
  const account = useAccount();
  const { updateSession } = useUser();
  const navigate = useNavigate();
  const { setError } = useError();
  const confirmDelete = useConfirmDelete();
  const [isLoading, setIsLoading] = useState(false);

  const projectID = useProjectState((state) => state.document?.id || null);
  const name = useProjectState((state) => state.document?.name || '');

  const handleDelete = useCallback(async () => {
    if (!projectID) return;

    setIsLoading(true);

    // Check the deployment count live
    const loadResponse = await api.project.load.query({ id: projectID });
    if (loadResponse.status !== 'ok') {
      setError('Unable to delete project. Please try again…', { errorKey: 'delete-project', status: loadResponse.status });
      setIsLoading(false);
      return;
    }

    // TODO: Need UX for this
    if (loadResponse.document.deploymentCount > 0) {
      alert('You have to delete all deployments in this project before it can be deleted.');
      setIsLoading(false);
      return;
    }

    const isOk = await confirmDelete({ targetName: name || '', targetType: 'project' });
    if (!isOk) {
      setIsLoading(false);
      return;
    }

    const response = await api.project.delete.mutate({
      projectID,
    });
    if (response.status !== 'ok') {
      setError('Unable to delete project. Please try again…', { errorKey: 'delete-project', status: response.status });
      setIsLoading(false);
      return;
    }

    updateSession();
    navigate(`/${account?.identifier}/projects`);
  }, [projectID, name, confirmDelete, navigate, updateSession, account?.identifier]);

  return (
    <div css={containerStyle}>
      <Button danger icon={<Trash />} loading={isLoading} onClick={handleDelete}>
        Delete Project
      </Button>
    </div>
  );
};

const containerStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
`;
